// components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaTachometerAlt,
  FaExchangeAlt,
  FaChartLine,
  FaUsers,
  FaCreditCard,
  FaGavel,
  FaPercent,
  FaPlug,
  FaCogs,
  FaBook,
  FaWallet,
  FaLightbulb,
  FaExclamationTriangle,
  FaTrophy,
  FaChartBar,
  FaList,
  FaMobileAlt,
  FaFileInvoice,
  FaPlusCircle,
  FaTags,
  FaFlagCheckered,
  FaMobile,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaSlidersH,
  FaServer,
  FaCog,
  FaBuilding,
  FaImage,
  FaHandshake,
  FaUniversity,
  FaArrowUp,
  FaArrowDown,
  FaFileAlt,
  FaCrown,
  FaChevronRight,
  FaTimes
} from 'react-icons/fa';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: FaTachometerAlt, path: '/dashboard' },
  {
    id: 'users',
    label: 'User Center',
    icon: FaUsers,
    path: '/users',
    children: [
      { id: 'all-users', label: 'All Users', icon: FaUsers, path: '/users/all' },
      /*
      { id: 'top-user', label: 'Top User', icon: FaTrophy, path: '/users/top' },
      { id: 'user-report', label: 'User Report', icon: FaChartBar, path: '/users/report' },
       */
    ],
  },
  {
    id: 'transaction',
    label: 'Transaction',
    icon: FaCreditCard,
    path: '/transaction',
    children: [
      { id: 'pending-transaction', label: 'Pending Transaction', icon: FaList, path: '/transaction/pending' },
      { id: 'mobile-recharge', label: 'Mobile & DTH', icon: FaMobileAlt, path: '/transaction/mobile-dth' },
      { id: 'bill-payment', label: 'Bill Payment', icon: FaFileInvoice, path: '/transaction/bill-payment' },
      { id: 'payment', label: 'Payment', icon: FaPlusCircle, path: '/transaction/payment' },
      { id: 'all-payment', label: 'All Payment', icon: FaPlusCircle, path: '/transaction/all-payment' },
      { id: 'transaction-report', label: 'Transaction Report', icon: FaPlusCircle, path: '/transaction/report' },
    ],
  },
  { id: 'ticket', label: 'Ticket', icon: FaTachometerAlt, path: '/ticket' },
  {
    id: 'margin',
    label: 'Margin',
    icon: FaPercent,
    path: '/margin',
    children: [
      { id: 'margin-plan', label: 'Margin Plan', icon: FaChartLine, path: '/margin/plan' },
      { id: 'margin-setup', label: 'Margin Setup', icon: FaSlidersH, path: '/margin/setup' },
    ],
  },
  {
    id: 'api',
    label: 'API',
    icon: FaPlug,
    path: '/api',
    children: [
      { id: 'all-provider', label: 'All Provider', icon: FaServer, path: '/api/providers' },
      { id: 'api-balance', label: 'API Balance', icon: FaCog, path: '/api/balance' },
    ],
  },
  {
    id: 'setting',
    label: 'Setting',
    icon: FaCogs,
    path: '/setting',
    children: [
      { id: 'operator', label: 'Operator', icon: FaBuilding, path: '/setting/operator' },
      { id: 'banner', label: 'Banner', icon: FaImage, path: '/setting/banner' },
      { id: 'service-provider', label: 'Service Provider', icon: FaHandshake, path: '/setting/service-provider' },
    ],
  },
  {
    id: 'khatabook',
    label: 'Khatabook',
    icon: FaBook,
    path: '/khatabook',
    children: [
      { id: 'bank', label: 'Bank', icon: FaUniversity, path: '/khatabook/bank' },
      { id: 'party', label: 'Party', icon: FaUsers, path: '/khatabook/party' },
      { id: 'payment-out', label: 'Payment Out', icon: FaArrowUp, path: '/khatabook/payment?type=OUT' },
      { id: 'payment-in', label: 'Payment In', icon: FaArrowDown, path: '/khatabook/payment?type=IN' },
      { id: 'report', label: 'Report', icon: FaFileAlt, path: '/khatabook/report' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: FaChartLine,
    path: '/analytics',
    children: [
      { id: 'user-balance', label: 'User Balance', icon: FaWallet, path: '/analytics/user-balance' },
      { id: 'recharge', label: 'Recharge', icon: FaLightbulb, path: '/analytics/recharge' },
      { id: 'bill-payment', label: 'Bill Payment', icon: FaExclamationTriangle, path: '/analytics/bill-payment' },
      { id: 'payment', label: 'Payment', icon: FaExclamationTriangle, path: '/analytics/payment' },
    ],
  },
];

const Sidebar = ({ currentPage, setCurrentPage, isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [isHovered, setIsHovered] = useState(false);

  const toggleDropdown = (id) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleNavClick = (id, path) => {
    setCurrentPage(id);
    // Navigate to the path
    if (path) {
      navigate(path);
    }
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  };

  const isChildActive = (children) => {
    return children?.some((child) => child.id === currentPage);
  };

  // Close dropdowns when sidebar collapses on desktop
  useEffect(() => {
    if (window.innerWidth >= 1024 && !isHovered) {
      setOpenDropdowns({});
    }
  }, [isHovered]);

  // Determine if text should be visible
  const isTextVisible = isOpen || (window.innerWidth >= 1024 && isHovered);

  // Close sidebar on window resize (switch to desktop)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsOpen]);

  return (
    <>
      {/* Mobile sidebar - overlay handled in parent */}
      <aside
        className={`fixed top-0 left-0 h-full bg-white border-r border-gray-200 shadow-2xl flex flex-col overflow-y-auto z-50 transition-all duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 lg:w-20 hover:lg:w-72 lg:shadow-xl`}
        onMouseEnter={() => window.innerWidth >= 1024 && setIsHovered(true)}
        onMouseLeave={() => window.innerWidth >= 1024 && setIsHovered(false)}
      >
        {/* Brand Area */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-indigo-800 flex items-center justify-center shadow-md flex-shrink-0">
              <FaCrown className="text-white text-sm w-4 h-4" />
            </div>
            <span
              className={`font-bold text-transparent bg-clip-text bg-gradient-to-r from-slate-800 to-slate-600 text-xl whitespace-nowrap transition-all duration-300 ${isTextVisible ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0'
                }`}
            >
              Admin
            </span>
          </div>
          <button
            className="lg:hidden text-gray-400 hover:text-red-500 transition flex-shrink-0"
            onClick={() => setIsOpen(false)}
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-5 px-3 space-y-1.5 overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.id} className="dropdown-item">
              {item.children ? (
                <>
                  <div
                    className={`flex items-center justify-between cursor-pointer px-3 py-2.5 rounded-xl transition-all duration-200 ${isChildActive(item.children) || openDropdowns[item.id]
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    onClick={() => toggleDropdown(item.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span
                        className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${isTextVisible ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0 overflow-hidden'
                          }`}
                      >
                        {item.label}
                      </span>
                    </div>
                    <FaChevronRight
                      className={`text-xs transition-all duration-200 text-gray-400 flex-shrink-0 ${openDropdowns[item.id] ? 'rotate-90' : ''
                        } ${isTextVisible ? 'opacity-100' : 'opacity-0'}`}
                    />
                  </div>
                  <div
                    className={`ml-3 pl-2 space-y-1 mt-1 overflow-hidden transition-all duration-300 ${openDropdowns[item.id] ? 'max-h-96' : 'max-h-0'
                      }`}
                  >
                    {item.children.map((child) => (
                      <a
                        key={child.id}
                        href="#"
                        className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition ${currentPage === child.id
                          ? 'text-indigo-600 bg-indigo-50'
                          : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'
                          } whitespace-nowrap`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(child.id, child.path);
                        }}
                      >
                        <child.icon className="w-4 h-4 flex-shrink-0" />
                        <span
                          className={`transition-all duration-300 ${isTextVisible ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0 overflow-hidden'
                            }`}
                        >
                          {child.label}
                        </span>
                      </a>
                    ))}
                  </div>
                </>
              ) : (
                <a
                  href="#"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${currentPage === item.id
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.id, item.path);
                  }}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span
                    className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${isTextVisible ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0 overflow-hidden'
                      }`}
                  >
                    {item.label}
                  </span>
                </a>
              )}
            </div>
          ))}
        </nav>

        <style>{`
          .overflow-y-auto::-webkit-scrollbar { width: 4px; }
          .overflow-y-auto::-webkit-scrollbar-track { background: #f1f5f9; }
          .overflow-y-auto::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
          .max-w-0 { max-width: 0; }
          .max-w-xs { max-width: 20rem; }
        `}</style>
      </aside>
    </>
  );
};

export default Sidebar;